import { SetupInstance } from '../../app/msal-account-helpers';
import { useLocal } from './../environment';
import { protectedResources } from '../../authConfig';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import { msalConfig } from '../../app';

export class HeartBeatApi {

    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;

        // Do not pin to accounts[0]; rely on MSAL active account
        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiHeartBeats.scopes.send
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            // account is selected by AccessTokenHelper via instance.getActiveAccount()
        };

        this.baseUrl = useLocal ?
            "http://localhost:8084/api/signalr" :
            "https://apim.ianrobertson.co.uk/api/signalr/v1";

        this.state = "Not Started";
        this.interval = -1;

        this.accessTokenService = new AccessTokenHelper();
    }

    start = () => {
        console.log("Heartbeat Starting...");
        if ((this.state !== "Started" && this.state !== "Starting") || this.state === "Not Started") {
            this.state = "Starting";
            this.interval = setInterval(() => {

                this.#sendHeartBeat()
                    .then(() => {
                        if (this.state !== "Started") {
                            console.log("Heartbeats Started");
                        }
                        this.state = "Started";

                    })
                    .catch(err => {
                        this.state = `${err}`;
                        console.error(err)
                    });
            }, 10000);
        }

        return this.interval;
    };

    stop = interval => {
        clearInterval(interval ?? this.interval);
        this.state = "Stopped";
    };

    #sendHeartBeat = () => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;
        
        return polly()
            .logger(error => {
                console.error("An error occurred in sendHeartBeat", error);
                // InteractionRequiredAuthError is handled centrally by auth helpers
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })            
            .waitAndRetry(5)
            .executeForPromise(async () => {
                try {
                    const tokenResponse = await this.accessTokenService.getAccessToken(
                        this.instance, 
                        this.inProgress, 
                        this.accessTokenRequest
                    );
    
                    if (!tokenResponse) {
                        throw new Error("No token received");
                    }

                    // Don't proceed with connection if idTokenClaims or oid are falsey
                    if (!tokenResponse.idTokenClaims?.oid) {
                        console.error('Cannot establish Heartbeat connection: missing user ID in token claims');
                        this.state = "Error";
                        throw new Error('Missing user ID in token claims');
                    }
    
                    // Check if the token is about to expire (e.g., within 5 minutes)
                    const tokenClaims = tokenResponse.idTokenClaims;
                    const expirationTime = tokenClaims.exp * 1000; // Convert to milliseconds
                    const currentTime = Date.now();
                    const fiveMinutes = 5 * 60 * 1000;
    
                    let idToken = tokenResponse.accessToken;
    
                    if (expirationTime - currentTime < fiveMinutes) {
                        // Force refresh the token if it's about to expire
                        const refreshRequest = {
                            ...this.accessTokenRequest,
                            forceRefresh: true
                        };
                        
                        const newTokenResponse = await this.accessTokenService.getAccessToken(
                            this.instance,
                            this.inProgress,
                            refreshRequest
                        );
                        
                        idToken = newTokenResponse.accessToken;
                    }
    
                    const bearer = `Bearer ${idToken}`;
                    const headers = new Headers({
                        'Authorization': bearer,
                        'Content-Type': 'application/json'
                    });
    
                    const heartBeat = {
                        timestamp: new Date().toISOString()
                    };
    
                    const response = await fetch(`${this.baseUrl}/HeartBeat`, {
                        method: "POST",
                        headers: headers,
                        body: JSON.stringify(heartBeat)
                    });
    
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
    
                    return true;
    
                } catch (error) {
                    console.error('HeartBeat error:', error);
                    // InteractionRequiredAuthError is handled centrally by auth helpers
                    throw error;
                }
            });
    };
    
}